/**
 * Hero canvas animation controller (extracted from HeroSection for testability).
 *
 * Performance contract:
 * - Mobile and prefers-reduced-motion NEVER start the canvas loop (caller
 *   renders a static/CSS-only treatment instead).
 * - The loop pauses when the document is hidden and when the hero is offscreen,
 *   and at most one loop is ever running.
 * - Every frame id is tracked and cancelled; every listener/observer is removed
 *   on stop. Device pixel ratio is capped. Canvas dimensions are recomputed only
 *   on resize. No per-frame object/array allocations. Frame rate is capped.
 * - Bid cards are pre-rendered ONCE into offscreen sprite canvases: rotated
 *   fillText is the dominant draw cost (measured ~53 ms/frame for 150 cards in
 *   software rendering), so each frame blits cached sprites with drawImage.
 */

export interface Particle {
  x: number
  y: number
  baseX: number
  baseY: number
  size: number
  speed: number
  offset: number
  opacity: number
  rotation: number
  rotSpeed: number
  type: number
}

const BID_TITLES = [
  'Electrical Upgrade',
  'HVAC Replacement',
  'Plumbing Renovation',
  'General Construction',
  'Roofing Repair',
  'Fire Alarm Install',
  'Generator Install',
  'Window Replacement',
]

const AGENCIES = [
  'Town of Lexington, MA',
  'State of New Hampshire',
  'City of Portland, ME',
  'Vermont State Agency',
  'City of Providence, RI',
  'Town of Greenwich, CT',
]

const TRADES = ['ELECTRICAL', 'PLUMBING', 'HVAC', 'GENERAL', 'FIRE ALARM', 'ROOFING']
const DEADLINES = ['Due: Jan 15', 'Due: Feb 3', 'Due: Mar 22', 'Due: Apr 8', 'Due: May 17']

const MAX_DPR = 1.5
const FRAME_MS = 1000 / 30 // cap at ~30fps
const DESKTOP_PARTICLES = 150

interface CardSprite {
  canvas: HTMLCanvasElement
  w: number
  h: number
}

/** Draw one bid card ONCE into an offscreen canvas (see header note). */
function renderCardSprite(
  p: Particle,
  createCanvas: (w: number, h: number) => HTMLCanvasElement,
): CardSprite {
  const s = p.size
  const cardW = s * 0.7
  const cardH = s
  const pad = 4
  const w = Math.ceil(cardW + pad * 2)
  const h = Math.ceil(cardH + pad * 2)
  const sprite = createCanvas(w, h)
  const ctx = sprite.getContext('2d')
  if (!ctx) return { canvas: sprite, w, h }

  const ox = w / 2
  const oy = h / 2

  ctx.fillStyle = 'rgba(255,255,255,0.03)'
  ctx.strokeStyle = 'rgba(255,255,255,0.06)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.roundRect(ox - cardW / 2, oy - cardH / 2, cardW, cardH, 3)
  ctx.fill()
  ctx.stroke()

  const colors = ['rgba(30,64,175,0.5)', 'rgba(6,95,70,0.5)', 'rgba(124,45,18,0.5)']
  ctx.fillStyle = colors[p.type % 3]
  ctx.beginPath()
  ctx.roundRect(ox - cardW / 2, oy - cardH / 2, cardW, cardH * 0.1, [3, 3, 0, 0])
  ctx.fill()

  ctx.fillStyle = 'rgba(255,255,255,0.5)'
  ctx.font = `bold ${Math.max(8, s * 0.05)}px sans-serif`
  ctx.fillText(BID_TITLES[p.type % BID_TITLES.length], ox - cardW / 2 + 4, oy - cardH / 2 + cardH * 0.2)

  ctx.fillStyle = 'rgba(156,163,175,0.4)'
  ctx.font = `${Math.max(6, s * 0.035)}px sans-serif`
  ctx.fillText(AGENCIES[p.type % AGENCIES.length], ox - cardW / 2 + 4, oy - cardH / 2 + cardH * 0.32)

  ctx.fillStyle = 'rgba(254,243,199,0.3)'
  ctx.fillRect(ox - cardW / 2 + 4, oy - cardH / 2 + cardH * 0.4, cardW * 0.45, cardH * 0.1)
  ctx.fillStyle = 'rgba(146,64,14,0.5)'
  ctx.font = `bold ${Math.max(6, s * 0.03)}px sans-serif`
  ctx.fillText(DEADLINES[p.type % DEADLINES.length], ox - cardW / 2 + 6, oy - cardH / 2 + cardH * 0.47)

  ctx.fillStyle = 'rgba(219,234,254,0.25)'
  ctx.fillRect(ox - cardW / 2 + 4, oy - cardH / 2 + cardH * 0.55, cardW * 0.4, cardH * 0.08)
  ctx.fillStyle = 'rgba(30,64,175,0.5)'
  ctx.font = `bold ${Math.max(5, s * 0.025)}px sans-serif`
  ctx.fillText(TRADES[p.type % TRADES.length], ox - cardW / 2 + 6, oy - cardH / 2 + cardH * 0.61)

  return { canvas: sprite, w, h }
}

export interface HeroAnimationEnv {
  canvas: HTMLCanvasElement
  /** Element observed for viewport visibility (pause when offscreen). */
  section: HTMLElement
  isMobile: () => boolean
  isReducedMotion: () => boolean
  /** Called once when the loop actually starts (e.g. lazy-loaded extras). */
  onStart?: () => void
  // Injectable browser seams for tests:
  win?: Window
  doc?: Document
  raf?: (cb: FrameRequestCallback) => number
  caf?: (handle: number) => void
  observer?: typeof IntersectionObserver
  createCanvas?: (w: number, h: number) => HTMLCanvasElement
}

/**
 * Start the hero animation. Returns a stop function, or null when the
 * environment should use the static treatment (mobile / reduced motion).
 */
export function startHeroAnimation(env: HeroAnimationEnv): (() => void) | null {
  if (env.isMobile() || env.isReducedMotion()) {
    return null
  }

  const win: Window = env.win ?? window
  const doc: Document = env.doc ?? document
  const raf = env.raf ?? win.requestAnimationFrame.bind(win)
  const caf = env.caf ?? win.cancelAnimationFrame.bind(win)
  const ObserverImpl = env.observer ?? (typeof IntersectionObserver !== 'undefined' ? IntersectionObserver : undefined)
  const createCanvas = env.createCanvas ?? ((w: number, h: number) => {
    const c = doc.createElement('canvas')
    c.width = w
    c.height = h
    return c
  })

  const canvas = env.canvas
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  // Canvas dimensions: computed on init and resize only — never per frame.
  // DPR capped so high-density screens do not multiply fill cost.
  let width = 0
  let height = 0
  const resize = () => {
    const dpr = Math.min(win.devicePixelRatio || 1, MAX_DPR)
    width = canvas.offsetWidth || win.innerWidth
    height = canvas.offsetHeight || win.innerHeight
    canvas.width = Math.round(width * dpr)
    canvas.height = Math.round(height * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }
  resize()
  win.addEventListener('resize', resize)

  const particles: Particle[] = []
  const sprites: CardSprite[] = []
  for (let i = 0; i < DESKTOP_PARTICLES; i++) {
    const p: Particle = {
      x: 0,
      y: 0,
      baseX: Math.random() * (width || win.innerWidth),
      baseY: Math.random() * (height || win.innerHeight),
      size: 80 + Math.random() * 100,
      speed: 0.2 + Math.random() * 0.4,
      offset: Math.random() * Math.PI * 2,
      opacity: 0.15 + Math.random() * 0.35,
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.3,
      type: i,
    }
    particles.push(p)
    sprites.push(renderCardSprite(p, createCanvas))
  }

  // Scroll fade progress + velocity, sampled inside the frame loop (no
  // separate velocity rAF, no per-frame layout reads beyond cached scrollY).
  let scrollProgress = 0
  let scrollSpeed = 0
  let prevScrollY = win.scrollY
  const onScroll = () => {
    const vh = win.innerHeight || 1
    scrollProgress = Math.min(1, Math.max(0, win.scrollY / (2 * vh)))
  }
  win.addEventListener('scroll', onScroll, { passive: true })

  let rafId = 0
  let running = false
  let stopped = false
  let visible = true
  let lastFrame = 0
  let elapsed = 0

  const frame = (now: number) => {
    rafId = 0
    if (stopped || !running) return
    // Frame-rate cap: skip work until FRAME_MS has elapsed.
    if (now - lastFrame < FRAME_MS) {
      rafId = raf(frame)
      return
    }
    const dt = Math.min(0.1, (now - lastFrame) / 1000)
    lastFrame = now
    elapsed += dt

    const currentScrollY = win.scrollY
    scrollSpeed = (currentScrollY - prevScrollY) * 0.01
    prevScrollY = currentScrollY

    ctx.clearRect(0, 0, width, height)
    const warp = scrollSpeed * 30
    const fadeStart = 0.3
    const fadeProgress = Math.max(0, (scrollProgress - fadeStart) / (1 - fadeStart))

    for (let i = 0; i < particles.length; i++) {
      const p = particles[i]
      p.baseY += Math.sin(elapsed * p.speed + p.offset) * 0.3
      p.baseX += Math.cos(elapsed * p.speed * 0.5 + p.offset) * 0.15
      p.rotation += p.rotSpeed * 0.005
      p.x = p.baseX + warp * Math.sin(p.offset)
      p.y = p.baseY - Math.abs(warp) * 0.5
      const adjustedOpacity = p.opacity * (1 - fadeProgress)
      if (adjustedOpacity > 0.01) {
        const sprite = sprites[i]
        ctx.save()
        ctx.globalAlpha = adjustedOpacity
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rotation)
        ctx.drawImage(sprite.canvas, -sprite.w / 2, -sprite.h / 2)
        ctx.restore()
      }
    }

    rafId = raf(frame)
  }

  const start = () => {
    if (stopped || running) return
    running = true
    lastFrame = 0 // force immediate draw on resume
    rafId = raf(frame)
  }
  const pause = () => {
    running = false
    if (rafId) {
      caf(rafId)
      rafId = 0
    }
  }
  const syncRunState = () => {
    if (doc.hidden || !visible) pause()
    else start()
  }

  const onVisibility = () => syncRunState()
  doc.addEventListener('visibilitychange', onVisibility)

  let io: IntersectionObserver | undefined
  if (ObserverImpl) {
    io = new ObserverImpl(
      (entries) => {
        visible = entries[0]?.isIntersecting ?? true
        syncRunState()
      },
      { threshold: 0.01 },
    )
    io.observe(env.section)
  }

  env.onStart?.()
  syncRunState()

  // Stop: cancel the tracked frame, remove every listener/observer. After
  // stop() the loop can never restart.
  return () => {
    stopped = true
    running = false
    if (rafId) {
      caf(rafId)
      rafId = 0
    }
    win.removeEventListener('resize', resize)
    win.removeEventListener('scroll', onScroll)
    doc.removeEventListener('visibilitychange', onVisibility)
    io?.disconnect()
  }
}
